<template>
    <section class="ticket-viewer">
  
      <aside class="action-ribbon">

        <div class="ribbon-heading">
          <strong>App Client</strong>
          <span>Actions</span>
        </div>
  
        <button
          type="button"
          class="ribbon-action"
          :class="{ active: activeAction === 'name' }"
          @click="selectAction('name')"
        >
          <span class="material-icons-outlined">
            badge
          </span>
  
          <span class="ribbon-label">
            Name
          </span>
        </button>

        <button
          type="button"
          class="ribbon-action"
        :class="{ active: activeAction === 'password' }"
        @click="selectAction('password')"
      >
          <span class="material-icons-outlined">
            key
          </span>

          <span class="ribbon-label">
            Password
          </span>
        </button>

        <button
          type="button"
          class="ribbon-action"
        :class="{ active: activeAction === 'logout' }"
        @click="selectAction('logout')"
      >
          <span class="material-icons-outlined">
            logout
          </span>

          <span class="ribbon-label">
            Log Out
          </span>
        </button>
  
        <button
          type="button"
          class="ribbon-action danger"
          :class="{ active: activeAction === 'archive' }"
          @click="selectAction('archive')"
        >
          <span class="material-icons-outlined">
            archive
          </span>
  
          <span class="ribbon-label">
            Archive
          </span>
        </button>
  
      </aside>
  
      <div class="viewer-body">
  
        <div
          v-if="loading"
          class="state-card"
        >
          Loading App Client...
        </div>

        <div
          v-else-if="errorMessage"
          class="state-card error"
        >
          {{ errorMessage }}
        </div>
  
        <div
          v-else-if="client"
          class="viewer-content"
        >
  
          <section class="client-card">
  
            <div class="client-card-header">
  
              <div>
                <span class="eyebrow">
                  App Client
                </span>
  
                <h2>
                  {{ localClientName || "Client Record" }}
                </h2>
              </div>
  
              <div class="client-ticket-summary">
  
                <span class="summary-pill">
                  {{ sortedTickets.length }}
                  {{ sortedTickets.length === 1 ? "Ticket" : "Tickets" }}
                </span>
  
                <span
                  v-if="openTicketCount > 0"
                  class="summary-pill open"
                >
                  {{ openTicketCount }} Open
                </span>
  
              </div>
  
            </div>
  
            <div class="client-grid">
  
              <div class="client-field">
                <span>Name</span>
                <strong>{{ localClientName || "—" }}</strong>
              </div>
  
              <div class="client-field">
                <span>Email</span>
                <strong>{{ client.contactEm || "—" }}</strong>
              </div>
  
              <div class="client-field">
                <span>Phone</span>
                <strong>{{ formatPhoneNumber(client.mobile) }}</strong>
              </div>
  
              <div class="client-field">
                <span>State</span>
                <strong>{{ client.state || "—" }}</strong>
              </div>
  
            </div>
  
          </section>
  
          <section class="support-history">
  
            <div class="section-heading">
  
              <div>
                <span class="eyebrow">
                  Support
                </span>
  
                <h3>
                  Support Tickets
                </h3>
  
                <p>
                  Account assistance requests submitted by this App Client.
                </p>
              </div>
  
              <span class="ticket-total">
                {{ sortedTickets.length }}
              </span>
  
            </div>
  
            <div
              v-if="sortedTickets.length === 0"
              class="state-card"
            >
              No support tickets were found for this client.
            </div>
  
            <div
              v-else
              class="ticket-list"
            >
  
              <article
                v-for="supportTicket in sortedTickets"
                :key="supportTicket.id"
                class="support-ticket-card"
                :class="{
                  selected:
                    supportTicket.id === ticket?.id,
                  expanded:
                    isTicketExpanded(supportTicket)
                }"
              >
  
                <div class="ticket-code-block">
  
                  <span class="field-label">
                    Ticket
                  </span>
  
                  <strong class="ticket-code">
                    #{{ supportTicket.code }}
                  </strong>
  
                </div>
  
                <div class="ticket-issue-block">
  
                  <span class="field-label">
                    Issue
                  </span>
  
                  <strong class="ticket-issue">
                    {{
                      supportTicket.issue ||
                      "No issue provided."
                    }}
                  </strong>
  
                </div>
  
                <div class="ticket-status-block">
  
                  <span
                    class="status-pill"
                    :class="ticketStatusClass(supportTicket)"
                  >
                    {{ ticketStatusLabel(supportTicket) }}
                  </span>
  
                  <span class="ticket-date">
                    {{ formatTicketDate(supportTicket.createdAt) }}
                  </span>
  
                </div>

                <button
                  type="button"
                  class="ticket-expand-button"
                  :aria-expanded="isTicketExpanded(supportTicket)"
                  @click="toggleTicket(supportTicket)"
                >
                  <span class="material-icons-outlined">
                    {{ isTicketExpanded(supportTicket) ? "expand_less" : "expand_more" }}
                  </span>

                  <span>
                    {{ isTicketExpanded(supportTicket) ? "Minimize" : "Expand" }}
                  </span>
                </button>

                <section
                  v-if="isTicketExpanded(supportTicket)"
                  class="expanded-ticket-details"
                >
                  <div class="ticket-detail full-issue">
                    <span>Issue</span>
                    <p>{{ supportTicket.issue || "No issue provided." }}</p>
                  </div>

                  <section class="expanded-panel ticket-status-editor">
                    <div class="expanded-panel-heading">
                      <h4>Ticket Status</h4>

                      <span
                        class="status-pill"
                        :class="ticketStatusClass(supportTicket)"
                      >
                        {{ ticketStatusLabel(supportTicket) }}
                      </span>
                    </div>

                    <div class="status-update-controls">
                      <select
                        v-model="statusDrafts[supportTicket.id]"
                        :disabled="isTicketSaving(supportTicket)"
                        aria-label="Ticket status"
                      >
                        <option
                          v-for="option in ticketStatusOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>

                      <button
                        type="button"
                        class="update-status-button"
                        :disabled="
                          isTicketSaving(supportTicket) ||
                          statusDrafts[supportTicket.id] === supportTicket.status
                        "
                        @click="saveTicketStatus(supportTicket)"
                      >
                        {{ isTicketSaving(supportTicket) ? "Saving..." : "Update Status" }}
                      </button>
                    </div>

                    <p
                      v-if="ticketStatusErrors[supportTicket.id]"
                      class="status-update-error"
                    >
                      {{ ticketStatusErrors[supportTicket.id] }}
                    </p>
                  </section>

                  <div class="ticket-detail">
                    <span>Created</span>
                    <strong>{{ formatFullDate(supportTicket.createdAt) }}</strong>
                  </div>

                  <div class="ticket-detail">
                    <span>Updated</span>
                    <strong>{{ formatFullDate(supportTicket.updatedAt) }}</strong>
                  </div>

                  <div class="ticket-detail">
                    <span>Expires</span>
                    <strong>{{ formatFullDate(supportTicket.expiresAt) }}</strong>
                  </div>

                  <div class="ticket-detail">
                    <span>Opened</span>
                    <strong>{{ formatFullDate(supportTicket.openedAt) }}</strong>
                  </div>

                  <div class="ticket-detail">
                    <span>Resolved</span>
                    <strong>{{ formatFullDate(supportTicket.resolvedAt) }}</strong>
                  </div>

                  <section class="expanded-panel email-activity">
                    <div class="expanded-panel-heading">
                      <h4>Email Activity</h4>
                    </div>

                    <div class="compact-detail-grid">
                      <div class="ticket-detail">
                        <span>Email Status</span>
                        <strong>{{ supportTicket.emailStatus || "—" }}</strong>
                      </div>

                      <div class="ticket-detail">
                        <span>Email Requested</span>
                        <strong>{{ formatFullDate(supportTicket.emailRequestedAt) }}</strong>
                      </div>

                      <div class="ticket-detail">
                        <span>Email Sent</span>
                        <strong>{{ formatFullDate(supportTicket.emailSentAt) }}</strong>
                      </div>

                      <div class="ticket-detail">
                        <span>Email Send Count</span>
                        <strong>{{ supportTicket.emailSendCount ?? 0 }}</strong>
                      </div>
                    </div>
                  </section>

                  <section class="expanded-panel admin-response-placeholder">
                    <div class="expanded-panel-heading">
                      <h4>Admin Response</h4>
                    </div>

                    <p>
                      Admin responses will be sent to the App Client by email and stored with this ticket.
                    </p>
                  </section>
                </section>
  
              </article>
  
            </div>
  
          </section>

          <MaixFooter />
  
        </div>
  
      </div>
  
      <div
        v-if="activeAction"
        class="drawer-scrim"
        @click="closeAction"
      />
  
      <section
        v-if="activeAction"
        class="action-drawer"
      >
  
        <div class="drawer-header">
  
          <div>
            <span class="eyebrow">
              Client Action
            </span>
  
            <h3>
              {{ actionTitle }}
            </h3>
          </div>
  
          <button
            type="button"
            class="drawer-close"
            @click="closeAction"
          >
            <span class="material-icons-outlined">
              close
            </span>
          </button>
  
        </div>
  
        <div
          v-if="activeAction === 'name'"
          class="drawer-content name-change-drawer"
        >

          <div class="name-change-heading">
            <span class="eyebrow">
              Name Change
            </span>

            <strong class="current-client-name">
              {{ localClientName || "Client Record" }}
            </strong>
          </div>

          <form class="name-change-form" @submit.prevent="submitNameChange">
            <label class="name-change-field">
              <span>Current Name</span>

              <input
                v-model="nameChangeCurrentName"
                type="text"
                placeholder="Type the user's current name"
                autocomplete="off"
              />

              <small v-if="nameChangeCurrentNameMismatch" class="name-change-error">
                Current name does not match.
              </small>
            </label>

            <label class="name-change-field">
              <span>New Requested Name</span>

              <input
                v-model="nameChangeNewName"
                type="text"
                placeholder="Type the user's new requested name"
                autocomplete="off"
              />
            </label>

            <label class="name-change-field">
              <span>Confirm New Requested Name</span>

              <input
                v-model="nameChangeConfirmName"
                type="text"
                placeholder="Confirm the user's new requested name"
                autocomplete="off"
              />

              <small v-if="nameChangeConfirmationMismatch" class="name-change-error">
                New names do not match.
              </small>
            </label>

            <div class="name-change-actions">
              <button
                type="submit"
                class="update-status-button name-change-submit-button"
                :disabled="!nameChangeFormValid || nameChangeSaving"
              >
                {{ nameChangeSaving ? "Updating..." : "Update Name" }}
              </button>
            </div>

            <p v-if="nameChangeError" class="name-change-submit-error" role="alert">
              {{ nameChangeError }}
            </p>
          </form>

        </div>

        <div
          v-else-if="activeAction === 'password'"
          class="drawer-content"
        >
          <p>
            Manage the App Client's password access.
          </p>

          <div class="action-placeholder">
            Password management will be available here.
          </div>
        </div>

        <div
          v-else-if="activeAction === 'logout'"
          class="drawer-content force-logout-drawer"
        >
          <span class="eyebrow">
            Log Out
          </span>

          <p>
            This immediately signs the App Client out of every device.
          </p>

          <p>
            The App Client must sign in again before using the application.
          </p>

          <form class="force-logout-form" @submit.prevent="submitForceLogout">
            <label class="force-logout-confirmation">
              <input
                v-model="forceLogoutConfirmed"
                type="checkbox"
                :disabled="forceLogoutSaving"
              />

              <span>
                I understand this action will immediately require the App Client to sign in again.
              </span>
            </label>

            <div class="force-logout-actions">
              <button
                type="submit"
                class="update-status-button force-logout-button"
                :disabled="!forceLogoutConfirmed || forceLogoutSaving"
              >
                {{ forceLogoutSaving ? "Signing Out..." : "Force Log Out" }}
              </button>
            </div>

            <p v-if="forceLogoutSuccess" class="force-logout-success" role="status">
              {{ forceLogoutSuccess }}
            </p>

            <p v-if="forceLogoutError" class="force-logout-error" role="alert">
              {{ forceLogoutError }}
            </p>
          </form>
        </div>
  
        <div
          v-else-if="activeAction === 'archive'"
          class="drawer-content"
        >
  
          <p>
            Archive this App Client account.
          </p>
  
          <div class="action-warning">
            Archiving will remove the account from active App Client counts.
          </div>
  
          <div class="action-placeholder">
            Archive controls will go here.
          </div>
  
        </div>
  
      </section>
  
    </section>
  </template>
  
  <script>
  import {
    computed,
    ref,
    watch
  } from "vue";

  import {
    fetchPreparedAppClientAccountAction,
    fetchPreparedAppClientFieldChange,
    requestAppClientAccountAction,
    requestAppClientFieldChange,
    updateSupportTicketStatus
  } from "@/firebase/firestore";

  import MaixFooter from "@/components/utils/maixFooter.vue";
  
  export default {
    name: "AppClientTicketViewer",

    components: {
      MaixFooter
    },
  
    props: {
      ticket: {
        type: Object,
        required: true
      },
  
      tickets: {
        type: Array,
        default: () => []
      },
  
      client: {
        type: Object,
        default: null
      },
  
      loading: {
        type: Boolean,
        default: false
      },
  
      errorMessage: {
        type: String,
        default: ""
      }
    },
  
    setup(props) {
  
      const activeAction = ref("");
      const expandedTicketIds = ref(new Set());
      const localTickets = ref([]);
      const statusDrafts = ref({});
      const savingTicketIds = ref(new Set());
      const ticketStatusErrors = ref({});
      const localClientName = ref("");
      const nameChangeCurrentName = ref("");
      const nameChangeNewName = ref("");
      const nameChangeConfirmName = ref("");
      const nameChangeSaving = ref(false);
      const nameChangeError = ref("");
      const forceLogoutConfirmed = ref(false);
      const forceLogoutSaving = ref(false);
      const forceLogoutSuccess = ref("");
      const forceLogoutError = ref("");

      const ticketStatusOptions = [
        { value: "open", label: "Open" },
        { value: "replied", label: "Replied" },
        { value: "pending", label: "Pending" },
        { value: "resolved", label: "Resolved" },
        { value: "closed", label: "Closed" }
      ];

      const canonicalTicketStatus =
        ticket => {
          const status = String(ticket?.status ?? "open").toLowerCase();

          return status === "emailed" || status === "expired"
            ? "open"
            : status;
        };

      watch(
        () => props.tickets,
        tickets => {
          localTickets.value = tickets.map(ticket => ({
            ...ticket,
            status: canonicalTicketStatus(ticket)
          }));
          statusDrafts.value = Object.fromEntries(
            tickets.map(ticket => [ticket.id, canonicalTicketStatus(ticket)])
          );
        },
        { immediate: true }
      );

      watch(
        () => props.ticket?.id,
        ticketId => {
          if (!ticketId) { return; }

          const nextIds = new Set(expandedTicketIds.value);
          nextIds.add(ticketId);
          expandedTicketIds.value = nextIds;
        },
        { immediate: true }
      );

      watch(
        () => props.client?.name,
        name => {
          localClientName.value = String(name ?? "");
        },
        { immediate: true }
      );

      const isTicketExpanded =
        ticket =>
          expandedTicketIds.value.has(ticket.id);

      const toggleTicket =
        ticket => {
          const nextIds = new Set(expandedTicketIds.value);

          if (nextIds.has(ticket.id)) {
            nextIds.delete(ticket.id);
          } else {
            nextIds.add(ticket.id);
          }

          expandedTicketIds.value = nextIds;
        };

      const workflowStatus =
        ticket =>
          canonicalTicketStatus(ticket) === "open" && !ticket.openedAt
            ? "new"
            : canonicalTicketStatus(ticket);

      const workflowPriority = {
        new: 0,
        open: 1,
        replied: 2,
        pending: 3,
        resolved: 4,
        closed: 5
      };

      const ticketTimestamp =
        value =>
          value?.toMillis?.() ??
          value?.toDate?.()?.getTime?.() ??
          (value instanceof Date ? value.getTime() : 0);

      const sortedTickets =
        computed(() =>
          [...localTickets.value].sort((left, right) => {
            const selectedTicketId = props.ticket?.id;
            if (left.id === selectedTicketId) { return -1; }
            if (right.id === selectedTicketId) { return 1; }

            const leftPriority = workflowPriority[workflowStatus(left)] ?? 6;
            const rightPriority = workflowPriority[workflowStatus(right)] ?? 6;

            if (leftPriority !== rightPriority) {
              return leftPriority - rightPriority;
            }

            return ticketTimestamp(right.createdAt) - ticketTimestamp(left.createdAt);
          })
        );

      const isTicketSaving =
        ticket =>
          savingTicketIds.value.has(ticket.id);

      const saveTicketStatus =
        async ticket => {
          const status = statusDrafts.value[ticket.id];
          if (!status || status === ticket.status || isTicketSaving(ticket)) { return; }

          const nextSavingIds = new Set(savingTicketIds.value);
          nextSavingIds.add(ticket.id);
          savingTicketIds.value = nextSavingIds;

          ticketStatusErrors.value = {
            ...ticketStatusErrors.value,
            [ticket.id]: ""
          };

          try {
            await updateSupportTicketStatus({
              clientId: ticket.clientId ?? props.client?.uid,
              ticketId: ticket.ticketId ?? ticket.id,
              status
            });

            const updatedAt = new Date();

            localTickets.value = localTickets.value.map(currentTicket =>
              currentTicket.id === ticket.id
                ? {
                    ...currentTicket,
                    status,
                    updatedAt,
                    resolvedAt:
                      status === "resolved"
                        ? updatedAt
                        : currentTicket.resolvedAt
                  }
                : currentTicket
            );
          } catch (error) {
            ticketStatusErrors.value = {
              ...ticketStatusErrors.value,
              [ticket.id]: error?.message ?? "Unable to update the ticket status."
            };
          } finally {
            const remainingSavingIds = new Set(savingTicketIds.value);
            remainingSavingIds.delete(ticket.id);
            savingTicketIds.value = remainingSavingIds;
          }
        };
  
      const openTicketCount =
        computed(() =>
          sortedTickets.value.filter(ticket =>
            canonicalTicketStatus(ticket) === "open"
          ).length
        );
  
      const actionTitle =
        computed(() => {
  
          switch (activeAction.value) {
            case "name":
              return "Change Client Name";

            case "password":
              return "Password";

            case "logout":
              return "Log Out";
  
            case "archive":
              return "Archive Account";
  
            default:
              return "";
          }
        });

      const normalizedName =
        value => String(value ?? "").trim().toLowerCase();

      const nameChangeCurrentNameMatches =
        computed(() =>
          normalizedName(nameChangeCurrentName.value) !== "" &&
          normalizedName(nameChangeCurrentName.value) === normalizedName(localClientName.value)
        );

      const nameChangeConfirmationMatches =
        computed(() =>
          normalizedName(nameChangeConfirmName.value) !== "" &&
          normalizedName(nameChangeConfirmName.value) === normalizedName(nameChangeNewName.value)
        );

      const nameChangeCurrentNameMismatch =
        computed(() =>
          nameChangeCurrentName.value.trim() !== "" &&
          !nameChangeCurrentNameMatches.value
        );

      const nameChangeConfirmationMismatch =
        computed(() =>
          nameChangeConfirmName.value.trim() !== "" &&
          !nameChangeConfirmationMatches.value
        );

      const nameChangeFormValid =
        computed(() => {
          const currentName = normalizedName(localClientName.value);
          const newName = normalizedName(nameChangeNewName.value);

          return nameChangeCurrentNameMatches.value &&
            newName !== "" &&
            nameChangeConfirmationMatches.value &&
            newName !== currentName;
        });

      const resetNameChange =
        () => {
          nameChangeCurrentName.value = "";
          nameChangeNewName.value = "";
          nameChangeConfirmName.value = "";
          nameChangeError.value = "";
        };

      const submitNameChange =
        async () => {
          if (!nameChangeFormValid.value || nameChangeSaving.value) { return; }

          const clientId = String(props.client?.uid ?? props.ticket?.clientId ?? "").trim();
          const ticketId = String(props.ticket?.ticketId ?? props.ticket?.id ?? "").trim();
          const newName = nameChangeNewName.value.trim();

          nameChangeSaving.value = true;
          nameChangeError.value = "";

          try {
            console.log("[NAME CHANGE] 🚀 Starting Name Change", {
              clientId,
              ticketId,
              newName
            });

            const requestId = await requestAppClientFieldChange({
              clientId,
              ticketId,
              field: "name",
              value: newName
            });

            console.log("[NAME CHANGE] ✅ Name Change request submitted", {
              requestId,
              response: requestId
            });

            let updatedClient = null;

            for (let attempt = 0; attempt < 30; attempt += 1) {
              console.log("[NAME CHANGE] 🔍 Polling Name Change request...", { requestId });

              updatedClient = await fetchPreparedAppClientFieldChange(requestId);

              console.log("[NAME CHANGE] 📄 Name Change status:", updatedClient);

              if (updatedClient) { break; }

              await new Promise(resolve => setTimeout(resolve, 500));
            }

            if (!updatedClient?.name) {
              console.log("[NAME CHANGE] ⏰ Name Change polling timed out.", { requestId });
              throw new Error("The Name Change is still being processed. Please try again shortly.");
            }

            console.log("[NAME CHANGE] ✅ Name Change completed.", updatedClient);

            localClientName.value = updatedClient.name;
            resetNameChange();
            activeAction.value = "";

          } catch (error) {
            console.error("[NAME CHANGE] ❌ Name Change failed.", error);
            nameChangeError.value = error?.message ?? "Unable to update the App Client name.";

          } finally {
            nameChangeSaving.value = false;
          }
        };

      const resetForceLogout =
        () => {
          forceLogoutConfirmed.value = false;
          forceLogoutSuccess.value = "";
          forceLogoutError.value = "";
        };

      const submitForceLogout =
        async () => {
          if (!forceLogoutConfirmed.value || forceLogoutSaving.value) { return; }

          const clientId = String(props.client?.uid ?? props.ticket?.clientId ?? "").trim();
          const ticketId = String(props.ticket?.ticketId ?? props.ticket?.id ?? "").trim();

          forceLogoutSaving.value = true;
          forceLogoutSuccess.value = "";
          forceLogoutError.value = "";

          try {
            const requestId = await requestAppClientAccountAction({
              action: "force_logout",
              clientId,
              ticketId
            });

            let result = null;

            for (let attempt = 0; attempt < 30; attempt += 1) {
              result = await fetchPreparedAppClientAccountAction(requestId);
              if (result?.success) { break; }

              await new Promise(resolve => setTimeout(resolve, 500));
            }

            if (!result?.success) {
              throw new Error("Force Log Out is still being processed. Please try again shortly.");
            }

            forceLogoutSuccess.value = "Successfully signed the App Client out.";

          } catch (error) {
            forceLogoutError.value = error?.message ?? "Unable to sign the App Client out.";

          } finally {
            forceLogoutSaving.value = false;
          }
        };
  
      const selectAction =
        action => {

          const nextAction =
            activeAction.value === action
              ? ""
              : action;

          if (activeAction.value === "name" && nextAction !== "name") {
            resetNameChange();
          }

          if (activeAction.value === "logout" && nextAction !== "logout") {
            resetForceLogout();
          }

          activeAction.value = nextAction;
        };
  
      const closeAction =
        () => {

          if (activeAction.value === "name") {
            resetNameChange();
          }

          if (activeAction.value === "logout") {
            resetForceLogout();
          }

          activeAction.value = "";
        };
  
      const ticketDate =
        value =>
          value?.toDate?.() ??
          (
            value instanceof Date
              ? value
              : null
          );
  
      const formatTicketDate =
        value => {
  
          const date = ticketDate(value);
          if (!date) { return ""; }
  
          const minutes =
            Math.floor(
              (Date.now() - date.getTime()) / 60000
            );
  
          if (minutes < 1) { return "Just now"; }
          if (minutes < 60) { return `${minutes}m ago`; }
  
          const hours = Math.floor(minutes / 60);
          if (hours < 24) { return `${hours}h ago`; }
  
          const days = Math.floor(hours / 24);
          if (days < 7) { return `${days}d ago`; }
  
          return date.toLocaleDateString();
        };

      const formatFullDate =
        value => {
          const date = ticketDate(value);
          return date ? date.toLocaleString() : "—";
        };

      const formatPhoneNumber =
        value => {
          if (!value) { return "—"; }

          const digits = String(value).replace(/\D/g, "");
          const phoneDigits = digits.length === 11 && digits.startsWith("1")
            ? digits.slice(1)
            : digits;

          if (phoneDigits.length !== 10) { return value; }

          return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
        };
  
      const ticketStatusLabel =
        ticket => {
  
          if (
            ticket.status === "open" &&
            !ticket.openedAt
          ) {
            return "New";
          }
  
          switch (canonicalTicketStatus(ticket)) {
            case "open":
              return "Open";

            case "replied":
              return "Replied";

            case "pending":
              return "Pending";
  
            case "resolved":
              return "Resolved";

            case "closed":
              return "Closed";
  
            default:
              return ticket.status || "Unknown";
          }
        };
  
      const ticketStatusClass =
        ticket => {
  
          if (
            ticket.status === "open" &&
            !ticket.openedAt
          ) {
            return "new";
          }
  
          return canonicalTicketStatus(ticket) || "unknown";
        };
  
      return {
        activeAction,
        isTicketExpanded,
        toggleTicket,
        statusDrafts,
        ticketStatusOptions,
        ticketStatusErrors,
        isTicketSaving,
        saveTicketStatus,
        sortedTickets,
        openTicketCount,
        actionTitle,
        localClientName,
        nameChangeCurrentName,
        nameChangeNewName,
        nameChangeConfirmName,
        nameChangeSaving,
        nameChangeError,
        nameChangeCurrentNameMismatch,
        nameChangeConfirmationMismatch,
        nameChangeFormValid,
        submitNameChange,
        forceLogoutConfirmed,
        forceLogoutSaving,
        forceLogoutSuccess,
        forceLogoutError,
        submitForceLogout,
        selectAction,
        closeAction,
        formatTicketDate,
        formatFullDate,
        formatPhoneNumber,
        ticketStatusLabel,
        ticketStatusClass
      };
    }
  };
  </script>
  
  <style scoped>
  .ticket-viewer {
    position: relative;
    min-height: 100%;
  }
  
  .viewer-body {
    padding-left: 88px;
  }
  
  .viewer-content {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }
  
  .action-ribbon {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 30;
  
    display: flex;
    flex-direction: column;
    gap: 10px;
  
    width: 72px;
  }

  .ribbon-heading {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0 3px 2px;
  }

  .ribbon-heading strong {
    color: #26332c;
    font-size: 0.59rem;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .ribbon-heading span {
    color: #87918c;
    font-size: 0.66rem;
  }
  
  .ribbon-action {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
  
    width: 100%;
    min-height: 70px;
    padding: 13px 7px;
  
    border: 1px solid #dfe5e1;
    border-radius: 11px;
    background: #ffffff;
  
    color: #445049;
    font: inherit;
    cursor: pointer;
  }
  
  .ribbon-action:hover,
  .ribbon-action.active {
    border-color: #cddbd3;
    background: #f3f8f5;
  }
  
  .ribbon-action .material-icons-outlined {
    color: #178554;
    font-size: 1.35rem;
  }
  
  .ribbon-label {
    font-size: 0.62rem;
    font-weight: 700;
  }

  .ribbon-action.danger .material-icons-outlined {
    color: #b54a4a;
  }
  
  .client-card {
    padding: 22px;
    border: 1px solid #dfe5e1;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 5px 18px rgba(29, 49, 39, 0.04);
  }
  
  .client-card-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
  
    padding-bottom: 18px;
    border-bottom: 1px solid #edf0ee;
  }
  
  .client-card-header h2 {
    margin: 5px 0 0;
    color: #1f2d26;
    font-size: 1.55rem;
    font-weight: 600;
  }
  
  .client-ticket-summary {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 7px;
  }
  
  .summary-pill {
    display: inline-flex;
    padding: 5px 9px;
    border-radius: 999px;
  
    background: #f0f2f1;
    color: #66716b;
  
    font-size: 0.72rem;
    font-weight: 700;
  }
  
  .summary-pill.open {
    background: #edf7f1;
    color: #178554;
  }
  
  .client-grid {
    display: grid;
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  
    gap: 12px;
    margin-top: 18px;
  }
  
  .client-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
  
    padding: 14px;
  
    border: 1px solid #e5eae7;
    border-radius: 10px;
    background: #fafbfa;
  }
  
  .client-field span,
  .field-label {
    color: #7f8984;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  
  .client-field strong {
    color: #26332c;
  }
  
  .eyebrow {
    color: #7f8984;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  
  .support-history {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .section-heading {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
  }
  
  .section-heading h3 {
    margin: 4px 0 0;
    color: #1f2d26;
    font-size: 1.1rem;
  }
  
  .section-heading p {
    margin: 4px 0 0;
    color: #7b8580;
    font-size: 0.84rem;
  }
  
  .ticket-total {
    display: flex;
    align-items: center;
    justify-content: center;
  
    min-width: 30px;
    height: 30px;
    padding: 0 8px;
  
    border-radius: 999px;
    background: #f0f2f1;
    color: #66716b;
  
    font-size: 0.75rem;
    font-weight: 800;
  }
  
  .ticket-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  
  .support-ticket-card {
    display: grid;
    grid-template-columns:
      minmax(100px, 0.7fr)
      minmax(0, 4fr)
      minmax(160px, 1fr)
      auto;
  
    align-items: center;
    gap: 18px;
  
    padding: 16px 18px;
  
    border: 1px solid #dfe5e1;
    border-radius: 12px;
    background: #f8faf9;
  }
  
  .support-ticket-card.selected {
    border-color: #aad4bd;
    background: #f2f8f4;
    box-shadow:
      inset 3px 0 0 #18ab69;
  }

  .support-ticket-card.expanded {
    align-items: start;
  }

  .ticket-expand-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    min-height: 34px;
    padding: 0 10px;
    border: 1px solid #cfe0d6;
    border-radius: 8px;
    background: #ffffff;
    color: #178554;
    font: inherit;
    font-size: 0.75rem;
    font-weight: 700;
    cursor: pointer;
  }

  .ticket-expand-button:hover {
    border-color: #9fcab2;
    background: #edf7f1;
  }

  .ticket-expand-button .material-icons-outlined {
    font-size: 1.1rem;
  }

  .expanded-ticket-details {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-column: 1 / -1;
    gap: 14px 18px;
    width: 100%;
    padding-top: 16px;
    border-top: 1px solid #dbe5df;
  }

  .ticket-detail {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .ticket-detail span {
    color: #7f8984;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .ticket-detail strong,
  .ticket-detail p {
    margin: 0;
    overflow-wrap: anywhere;
    color: #303d36;
    font-size: 0.84rem;
  }

  .ticket-detail.full-issue {
    grid-column: 1 / -1;
  }

  .ticket-detail.full-issue p {
    white-space: pre-wrap;
  }

  .expanded-panel {
    grid-column: 1 / -1;
    padding: 14px;
    border: 1px solid #e0e6e2;
    border-radius: 10px;
    background: #ffffff;
  }

  .expanded-panel-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .expanded-panel-heading h4 {
    margin: 0;
    color: #26332c;
    font-size: 0.88rem;
  }

  .status-update-controls {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .status-update-controls select {
    min-height: 38px;
    min-width: 170px;
    padding: 0 34px 0 11px;
    border: 1px solid #d5ddd8;
    border-radius: 8px;
    background: #ffffff;
    color: #29362f;
    font: inherit;
  }

  .update-status-button {
    min-height: 38px;
    padding: 0 14px;
    border: 0;
    border-radius: 8px;
    background: #18ab69;
    color: #ffffff;
    font: inherit;
    font-size: 0.78rem;
    font-weight: 700;
    cursor: pointer;
  }

  .update-status-button:hover:not(:disabled) {
    background: #15945c;
  }

  .update-status-button:disabled {
    cursor: default;
    opacity: 0.5;
  }

  .status-update-error {
    margin: 9px 0 0;
    color: #a33636;
    font-size: 0.78rem;
    font-weight: 600;
  }

  .email-activity {
    background: #f6f8f7;
  }

  .compact-detail-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
  }

  .admin-response-placeholder {
    border-style: dashed;
    background: #fbfcfb;
  }

  .admin-response-placeholder .expanded-panel-heading {
    margin-bottom: 6px;
  }

  .admin-response-placeholder p {
    margin: 0;
    color: #737d78;
    font-size: 0.8rem;
  }
  
  .ticket-code-block,
  .ticket-issue-block {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  
  .ticket-code {
    color: #178554;
    font-size: 0.94rem;
    font-weight: 800;
    letter-spacing: 0.05em;
  }
  
  .ticket-issue {
    overflow: hidden;
    color: #303d36;
    font-size: 0.9rem;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .ticket-status-block {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 9px;
  }
  
  .status-pill {
    display: inline-flex;
    padding: 5px 9px;
    border-radius: 999px;
  
    background: #f0f2f1;
    color: #66716b;
  
    font-size: 0.71rem;
    font-weight: 700;
  }
  
  .status-pill.new {
    background: #fff0e8;
    color: #a35220;
  }
  
  .status-pill.open {
    background: #edf7f1;
    color: #178554;
  }
  
  .status-pill.resolved {
    background: #edf7f1;
    color: #178554;
  }
  
  .ticket-date {
    color: #7f8984;
    font-size: 0.76rem;
    white-space: nowrap;
  }
  
  .drawer-scrim {
    position: absolute;
    inset: 0;
    z-index: 10;
  
    border-radius: 14px;
    background: rgba(235, 240, 237, 0.62);
  }
  
  .action-drawer {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 88px;
    z-index: 20;
  
    width: min(632px, 50%);
    box-sizing: border-box;
    padding: 22px;
  
    border: 1px solid #dce3df;
    border-radius: 14px;
    background: #ffffff;
  
    box-shadow:
      10px 0 35px rgba(25, 43, 34, 0.14);
  
    overflow-y: auto;
  }
  
  .drawer-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
  
    padding-bottom: 16px;
    border-bottom: 1px solid #e8ece9;
  }
  
  .drawer-header h3 {
    margin: 4px 0 0;
    color: #1f2d26;
  }
  
  .drawer-close {
    display: flex;
    align-items: center;
    justify-content: center;
  
    width: 34px;
    height: 34px;
  
    border: 0;
    border-radius: 8px;
    background: #f3f5f4;
    color: #6f7974;
  
    cursor: pointer;
  }
  
  .drawer-content {
    margin-top: 18px;
  }
  
  .drawer-content p {
    margin: 0 0 14px;
    color: #66716b;
  }

  .name-change-heading {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-bottom: 18px;
    border-bottom: 1px solid #edf0ee;
  }

  .current-client-name {
    color: #26332c;
    font-size: 1.25rem;
    font-weight: 650;
    line-height: 1.3;
  }

  .name-change-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 18px;
  }

  .name-change-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .name-change-field > span {
    color: #5e6963;
    font-size: 0.72rem;
    font-weight: 700;
  }

  .name-change-field input {
    width: 100%;
    min-height: 42px;
    box-sizing: border-box;
    padding: 0 11px;
    border: 1px solid #d5ddd8;
    border-radius: 8px;
    background: #ffffff;
    color: #29362f;
    font: inherit;
    font-size: 0.84rem;
    outline: none;
  }

  .name-change-field input:focus {
    border-color: #8fc6a8;
    box-shadow: 0 0 0 3px rgba(24, 171, 105, 0.1);
  }

  .name-change-field input::placeholder {
    color: #9ba49f;
  }

  .name-change-error {
    color: #a33636;
    font-size: 0.72rem;
    font-weight: 600;
  }

  .name-change-form .name-change-submit-error {
    margin: -4px 0 0;
    color: #a33636;
    font-size: 0.76rem;
    font-weight: 600;
  }

  .name-change-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 3px;
  }

  .name-change-submit-button {
    min-width: 116px;
  }

  .force-logout-drawer > .eyebrow {
    display: block;
    margin-bottom: 10px;
  }

  .force-logout-form {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 20px;
  }

  .force-logout-confirmation {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    border: 1px solid #dfe5e1;
    border-radius: 10px;
    background: #f8faf9;
    color: #4f5d55;
    font-size: 0.82rem;
    line-height: 1.45;
    cursor: pointer;
  }

  .force-logout-confirmation input {
    margin: 3px 0 0;
  }

  .force-logout-actions {
    display: flex;
    justify-content: flex-end;
  }

  .force-logout-button {
    min-width: 132px;
  }

  .force-logout-form .force-logout-success,
  .force-logout-form .force-logout-error {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
  }

  .force-logout-form .force-logout-success {
    color: #1b7a4b;
  }

  .force-logout-form .force-logout-error {
    color: #a33636;
  }
  
  .action-placeholder,
  .action-warning {
    padding: 15px;
    border-radius: 10px;
  }
  
  .action-placeholder {
    border: 1px solid #e2e7e4;
    background: #f8faf9;
    color: #69736e;
  }
  
  .action-warning {
    margin-bottom: 12px;
    background: #fff4e8;
    color: #955326;
  }
  
  .state-card {
    padding: 24px;
    border: 1px solid #dfe5e1;
    border-radius: 13px;
    background: #ffffff;
    color: #737d78;
  }
  
  .state-card.error {
    border-color: #efc5c5;
    background: #fff7f7;
    color: #a33636;
  }
  
  @media (max-width: 760px) {
  
    .viewer-body {
      padding-top: 78px;
      padding-left: 0;
    }
  
    .action-ribbon {
      flex-direction: row;
      width: 100%;
    }
  
    .ribbon-action {
      width: auto;
      min-width: 78px;
    }
  
    .client-grid {
      grid-template-columns: 1fr;
    }
  
    .support-ticket-card {
      grid-template-columns: 1fr;
    }

    .ticket-expand-button {
      justify-self: start;
    }

    .expanded-ticket-details {
      grid-template-columns: 1fr;
    }

    .status-update-controls {
      align-items: stretch;
      flex-direction: column;
    }

    .status-update-controls select {
      width: 100%;
    }

    .compact-detail-grid {
      grid-template-columns: 1fr;
    }
  
    .ticket-status-block {
      justify-content: flex-start;
    }
  
    .action-drawer {
      position: fixed;
      inset: 0;
      z-index: 100;
  
      width: auto;
      border-radius: 0;
    }
  
    .drawer-scrim {
      position: fixed;
      z-index: 90;
    }
  }
  </style>
